import { useState } from 'react';
import Icon from '../atoms/Icon';

const BookDemoModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    recruiter_name: '',
    email: '',
    phone_number: '',
    context: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [newsletter, setNewsletter] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/demo-leads/book-demo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          onClose();
          setSubmitted(false);
          setFormData({
            company_name: '',
            recruiter_name: '',
            email: '',
            phone_number: '',
            context: ''
          });
          setNewsletter(false);
        }, 3000);
      } else {
        setError(data.detail || 'Failed to submit demo request. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Demo booking error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full my-8 overflow-hidden">
        {submitted ? (
          <div className="p-12 text-center">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="check_circle" size={64} className="text-green-600" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-3">Thank You!</h3>
            <p className="text-lg text-gray-600">
              We've received your request. Our team will contact you shortly to schedule a personalized demo.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 max-h-[85vh] overflow-y-auto">
            {/* Left Side - Form */}
            <div className="p-8 lg:p-12 bg-white overflow-y-auto">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Request a Demo</h2>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Talk to an Aptirix expert about your requirements, technology stack, and timeline. Complete the form and we'll make sure to reach out.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0"
                >
                  <Icon name="close" size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2 text-sm">
                    <Icon name="error" size={18} className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="recruiter_name"
                      value={formData.recruiter_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                      placeholder="Acme Corp."
                    />
                  </div>
                </div>

                {/* Email and Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Work Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                      placeholder="name@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                {/* Project Details */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Project Details or Questions
                  </label>
                  <textarea
                    name="context"
                    value={formData.context}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none text-sm"
                    placeholder="Share your requirements"
                  />
                </div>

                {/* Newsletter Checkbox */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="newsletter"
                    checked={newsletter}
                    onChange={(e) => setNewsletter(e.target.checked)}
                    className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="newsletter" className="text-xs text-gray-600 leading-relaxed">
                    Yes, I'd like to receive news and updates by email
                  </label>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gray-900 text-white font-semibold py-3.5 px-6 rounded-full hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Get started'
                    )}
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-3">
                    By submitting this form you agree with our{' '}
                    <a href="#" className="text-indigo-600 hover:underline">Privacy Policy</a>
                  </p>
                </div>
              </form>
            </div>

            {/* Right Side - Benefits */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 lg:p-12 flex flex-col justify-center overflow-y-auto">
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Icon name="speed" size={24} className="text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Save 80% Time on Screening</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Effectively handle log data overload with Aptirix. We trim logs efficiently, aiding resource management and enhancing system performance.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Icon name="analytics" size={24} className="text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Data-Driven Hiring Decisions</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Easily route all of your metrics, logs, and traces to any destination or vendor, with no risk of lock-in. Reuse and extend the instrumentation you already have in-place.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Icon name="verified" size={24} className="text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">AI-Verified Skills Assessment</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Safeguard sensitive data on your servers. Simplify compliance and security by removing sensitive information before it leaves your servers.
                    </p>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="pt-8 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    Trusted by Leading Companies
                  </p>
                  <div className="flex items-center gap-6 opacity-60">
                    <div className="text-2xl font-bold text-gray-400">Company</div>
                    <div className="text-2xl font-bold text-gray-400">Brand</div>
                    <div className="text-2xl font-bold text-gray-400">Corp</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookDemoModal;
